import { Send, Loader2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({ onSend, disabled, placeholder = 'Ask me anything about sales...' }: ChatInputProps) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !disabled) {
      onSend(input.trim());
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative flex items-center">
      <textarea
        ref={textareaRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="w-full resize-none bg-white border-2 border-[#1875FF]/30 rounded-xl px-4 py-3 pr-12 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF] disabled:opacity-50 disabled:cursor-not-allowed max-h-32 overflow-hidden font-medium"
        style={{
          boxShadow: '0 0 20px rgba(24, 117, 255, 0.15), 0 0 40px rgba(5, 224, 209, 0.1)'
        }}
      />

      <button
        type="submit"
        disabled={disabled || !input.trim()}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center p-1.5 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm hover:shadow-md"
      >
        {disabled ? (
          <Loader2 className="w-4 h-4 text-white animate-spin" />
        ) : (
          <Send className="w-4 h-4 text-white" />
        )}
      </button>
    </form>
  );
}
